class DetailsDisclosure extends HTMLElement {
    constructor() {
        super();
        this.mainDetailsToggle = this.querySelector('details');
        this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

        this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
        this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    }

    onFocusOut() {
        setTimeout(() => {
            if (!this.contains(document.activeElement)) this.close();
        });
    }

    onToggle() {
        if (!this.animations) this.animations = this.content.getAnimations();

        if (this.mainDetailsToggle.hasAttribute('open')) {
            this.animations.forEach(animation => animation.play());
        } else {
            this.animations.forEach(animation => animation.cancel());
        }
    }

    close() {
        this.mainDetailsToggle.removeAttribute('open');
        this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
    }
}

customElements.define('details-disclosure', DetailsDisclosure);

class DetailsDisclosureBase extends HTMLElement {
    constructor(focusOut = true) {
        super();
        this.name = this.dataset.name ?? 'disclosure';
        this.containerClasses = [`has-open-${this.name}`];
        this.focusOut = focusOut;
        this.onSummaryClick = this.handleSummaryClick.bind(this);
        this.onFocusOut = this.handleFocusOut.bind(this);
        this.onKeyUp = this.handleKeyup.bind(this);

        this.disclosureContainer = this.closest('[data-disclosure-container]');
        this.detailsElement = this.querySelector('details');
        this.summaryElement = this.querySelector('summary');
    }

    connectedCallback() {
        this.bindEvents();
    }

    disconnectedCallback() {
        this.unbindEvents();
    }

    bindEvents() {
        this.summaryElement.addEventListener('click', this.onSummaryClick);

        if (this.focusOut) {
            this.detailsElement.addEventListener('focusout', this.onFocusOut);
        }

        this.detailsElement.addEventListener('keyup', this.onKeyUp);
    }

    unbindEvents() {
        this.summaryElement.removeEventListener('click', this.onSummaryClick);

        if (this.focusOut) {
            this.detailsElement.removeEventListener('focusout', this.onFocusOut);
        }

        this.detailsElement.removeEventListener('keyup', this.onKeyUp);
    }

    isOpen() {
        return this.detailsElement.hasAttribute('open');
    }

    handleSummaryClick(event) {
        event.preventDefault();
        this.isOpen() ? this.close() : this.open();
    }

    handleFocusOut() {
        setTimeout(() => {
            if (!this.contains(document.activeElement)) this.close();
        });
    }

    handleKeyup(event) {
        event.code.toUpperCase() === 'ESCAPE' && this.close();
    }

    open() {
        this.detailsElement.setAttribute('open', true);
        this.disclosureContainer.classList.add(...this.containerClasses);
    }

    close() {
        this.detailsElement.removeAttribute('open');
        this.summaryElement.setAttribute('aria-expanded', false);
        this.disclosureContainer.classList.remove(...this.containerClasses);
    }
}

class MenuDisclosure extends DetailsDisclosureBase {
    constructor() {
        super();

        this.item = this.closest('li');
        this.child = this.querySelector('summary + div');
        this.innerChild = this.child.firstElementChild;
        this.siblings = Array.from(
            this.disclosureContainer.querySelectorAll('menu-disclosure')
        ).filter(sibling => sibling !== this);
        this.allChildren = Array.from(
            this.disclosureContainer.querySelectorAll('menu-disclosure summary + div')
        );
        this.background = this.disclosureContainer.querySelector(
            '[data-menu-background]'
        );

        this.header = document.querySelector('.header-section');
        this.headerWrapper = document.querySelector('.header-wrapper');
        this.rootLinks = this.disclosureContainer.querySelectorAll(
            '.header__menu-item--link, .header__icon'
        );

        this.timeline = gsap.timeline({ paused: true });
        this.openTimeline = gsap.timeline({ paused: true });
        this.switchTimeline = gsap.timeline({
            paused: true,
            onComplete: () =>
                this.disclosureContainer.classList.remove('is-switching'),
        });
        this.onMouseOver = this.handleMouseOver.bind(this);
        this.onClose = this.handleClose.bind(this);
        this.onRootLinkHover = this.handleRootLinkHover.bind(this);
        this.onResize = this.handleResize.bind(this);
        this.hoverIntentListeners = null;

        this.getHeaderHeight();
        this.buildTimelines();
    }

    bindEvents() {
        super.bindEvents();

        if (typeof window.hoverintent !== 'undefined') {
            const main = hoverintent(this, this.onMouseOver, () => { });
            main.options({ interval: 50 });

            const rootLinks = [];
            this.rootLinks.forEach(link => {
                const rootLinkListener = hoverintent(
                    link,
                    this.onRootLinkHover,
                    () => { }
                );
                rootLinkListener.options({ interval: 50 });
                rootLinks.push(rootLinkListener);
            });

            this.hoverIntentListeners = { main, rootLinks };
        } else {
            this.addEventListener('mouseenter', this.onMouseOver);
            this.rootLinks.forEach(link =>
                link.addEventListener('mouseenter', this.onRootLinkHover)
            );
        }

        this.header.addEventListener('mouseleave', this.onClose);

        this.isSwitching = false;
    }

    unbindEvents() {
        super.unbindEvents();

        if (this.hoverIntentListeners) {
            this.hoverIntentListeners.main.remove();
            this.hoverIntentListeners.rootLinks.forEach(listener =>
                listener.remove()
            );
            this.hoverIntentListeners = null;
        } else {
            this.removeEventListener('mouseenter', this.onMouseOver);
            this.rootLinks.forEach(link =>
                link.removeEventListener('mouseenter', this.onRootLinkHover)
            );
        }

        this.header.removeEventListener('mouseleave', this.onClose);
    }

    handleSummaryClick(e) {
        const innerLink = e.currentTarget.querySelector('a');
        if (innerLink) return;

        e.preventDefault();
    }

    handleResize() {
        if (this.isOpen()) {
            this.updateHeight();
        }
    }

    getSubnavHeight() {
        const headerHeight = this.header.offsetHeight;
        return this.child.offsetHeight + headerHeight;
    }

    updateHeight() {
        gsap.set(this.background, {
            y: this.getSubnavHeight.bind(this),
        });
    }

    buildTimeline(timeline, switching) {
        timeline
            .to(this.background, {
                y: this.getSubnavHeight.bind(this),
                duration: switching ? 0.35 : 0.5,
                ease: 'power2.inOut',
            })
            .to(
                this.allChildren,
                {
                    opacity: 0,
                    duration: 0.35,
                    ease: 'power2.out',
                },
                0
            )
            .fromTo(
                this.child,
                {
                    opacity: 0,
                },
                {
                    opacity: 1,
                    duration: 0.35,
                    ease: switching ? 'power2.in' : 'power2.inOut',
                },
                switching ? 0 : 0.2
            );

        if (!switching) {
            timeline.fromTo(
                this.innerChild,
                {
                    y: -60,
                },
                {
                    y: 0,
                    duration: 0.5,
                    ease: 'power2.inOut',
                    onComplete: () => gsap.set(this.innerChild, { clearProps: 'y' }),
                },
                0.05
            );
        } else {
            gsap.set(this.innerChild, {
                clearProps: 'y'
            });
        }
    }

    hideSiblings() {
        this.allChildren
            .filter(child => child !== this.child)
            .forEach(child => gsap.set(child, { opacity: 0 }));
    }

    buildTimelines() {
        this.buildTimeline(this.openTimeline, false);
        this.buildTimeline(this.switchTimeline, true);
    }

    isOpen() {
        return this.item.classList.contains('is-open');
    }

    handleMouseOver() {
        this.open();
    }

    handleClose() {
        this.cancelClose();

        this.closingTime = setTimeout(() => {
            this.close();
        }, 150);
    }

    handleRootLinkHover() {
        if (this.isOpen()) {
            this.handleClose();
        }
    }

    getHeaderHeight() {
        if (
            document.documentElement.style.getPropertyValue(
                '--header-bottom-position-desktop'
            ) === ''
        ) {
            document.documentElement.style.setProperty(
                '--header-bottom-position-desktop',
                `${Math.floor(this.headerWrapper.getBoundingClientRect().bottom)}px`
            );
            document.documentElement.style.setProperty(
                '--header-height',
                `${Math.floor(this.header.offsetHeight)}px`
            );
        }
    }

    open() {
        this.getHeaderHeight();

        this.isSwitching = this.hasOpenSibling();

        this.cancelClose();
        this.closeSiblings();

        if (this.isOpen()) return;

        super.open();

        if (this.isSwitching) {
            this.disclosureContainer.classList.add('is-switching');
        }

        this.item.classList.add('is-open');
        this.headerWrapper.preventHide = true;

        window.addEventListener('resize', this.onResize);

        setTimeout(() => {
            this.disclosureContainer.classList.add('has-menu-buffer');

            if (this.isSwitching) {
                this.switchTimeline.invalidate().play(0);
            } else {
                this.openTimeline.invalidate().play(0);
            }
        });
    }

    hasOpenSibling() {
        return this.siblings.some(sibling => sibling.isOpen());
    }

    cancelClose() {
        clearTimeout(this.closingTime);
        this.closingTime = undefined;
    }

    closeSiblings() {
        this.siblings.forEach(sibling => sibling.close());
    }

    close() {
        this.cancelClose();

        if (!this.isOpen()) return;

        this.headerWrapper.preventHide = false;
        this.item.classList.remove('is-open');
        this.summaryElement.setAttribute('aria-expanded', false);

        window.removeEventListener('resize', this.onResize);

        setTimeout(() => {
            if (!this.hasOpenSibling()) {
                this.disclosureContainer.classList.remove(...this.containerClasses);
                this.timeline.pause();
                gsap.to(this.innerChild, {
                    duration: 0.5,
                    y: -60,
                    ease: 'power2.inOut',
                    onComplete: () => gsap.set(this.innerChild, { clearProps: 'y' }),
                });
                gsap.to(this.child, {
                    opacity: 0,
                    duration: 0.4,
                    ease: 'power2.inOut',
                });

                gsap.to(this.background, {
                    y: 0,
                    duration: 0.5,
                    ease: 'power2.inOut',
                    delay: 0.05,
                });
            }
        });

        this.closeAnimation();
    }

    closeAnimation() {
        let animationStart;

        const handleAnimation = time => {
            if (animationStart === undefined) {
                animationStart = time;
            }

            const elapsedTime = time - animationStart;

            if (elapsedTime < 400) {
                window.requestAnimationFrame(handleAnimation);
            } else if (!this.isOpen()) {
                this.detailsElement.removeAttribute('open');

                if (!this.hasOpenSibling()) {
                    this.disclosureContainer.classList.remove('has-menu-buffer');
                }
            }
        };

        window.requestAnimationFrame(handleAnimation);
    }
}

customElements.define('menu-disclosure', MenuDisclosure);